import React from "react";
import { useTranslation } from "react-i18next";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import HeaderStatisticsSection from "../../components/HeaderStatisticsSection";
import {
    faBolt,
    faCheck,
    faCircleCheck,
    faClock,
    faCoins,
    faPlugCirclePlus,
    faTransformerBolt,
    faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";


const iconColors = [
    "bg-primary-500",
    "bg-secondary-500",
    "bg-warning-500",
    "bg-lime-500", 
    "bg-mute-500",
];

const getPluginRateFromSummary = (summary) => {
    if (summary == null || typeof summary !== "object") {
        return 0;
    }
    const n = Number(summary.pluginSuccessRate);
    return Number.isFinite(n) ? n : 0;
};

const getPluginSuccessPresentation = (percent) => {
    const safe = Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0;
    let tone = "neutral";
    if (safe >= 85) {
        tone = "positive";
    } else if (safe >= 70) {
        tone = "neutral";
    } else if (safe >= 60) {
        tone = "caution";
    } else {
        tone = "critical";
    }
    return { tone, barPercent: safe };
};

const buildChargingSummaryItems = (stats, pluginSuccessRateRaw, t) => {
    const pluginPresentation = getPluginSuccessPresentation(
        pluginSuccessRateRaw == null ? 0 : pluginSuccessRateRaw
    );
    return [
        { icon: faCoins, title: t("sessions.summary.totalTransactionAmount"), value: stats.totalTransactionAmount },
        { icon: faWavePulse, title: t("sessions.summary.totalSessions"), value: stats.totalSessions },
        { icon: faCheck, title: t("sessions.summary.successfulSessions"), value: stats.successfulSessions },
        { icon: faTransformerBolt, title: t("sessions.summary.energyUsed"), value: stats.energyUsed },
        { icon: faPlugCirclePlus, title: t("sessions.summary.averageDailySessions"), value: stats.averageDailySessions },
        {
            icon: faCircleCheck,
            title: t("sessions.summary.pluginSuccessRate"),
            titleHint: t("sessions.summary.pluginSuccessRateHint"),
            value: stats.pluginSuccessRate,
            tone: pluginPresentation.tone,
            barPercent: pluginPresentation.barPercent,
        },
        { icon: faClock, title: t("sessions.summary.averageChargingTime"), value: stats.averageChargingTime },
        { icon: faBolt, title: t("sessions.summary.averageEnergyUsed"), value: stats.averageEnergyUsed },
    ];
};

const ChargingSessionsSummary = ({ summary, summaryGroups = {}, loading = false }) => {
    const { t } = useTranslation();

    const renderSummarySection = (resolvedSummary) => {
        const stats = mapSummaryToDisplay(resolvedSummary, t);
        const pluginRate = getPluginRateFromSummary(resolvedSummary);
        return <HeaderStatisticsSection items={buildChargingSummaryItems(stats, pluginRate, t)} />;
    };

    const hasApiSummary =
        summary != null ||
        Object.values(summaryGroups).some((group) => group != null);

    if (loading || !hasApiSummary) {
        return (
            <div className="flex min-w-full pb-12">
                <HeaderStatisticsSection items={buildChargingSummaryItems(mapSummaryToDisplay(null, t), 0, t)} />
            </div>
        );
    }

    const groupKeys = Object.keys(summaryGroups);

    if (groupKeys.length <= 1) {
        const resolvedSummary =
            (groupKeys[0] != null ? summaryGroups[groupKeys[0]] : null) ?? summary;

        return (
            <div className="flex min-w-full pb-12">
                {renderSummarySection(resolvedSummary)}
            </div>
        );
    }

    return (
        <Carousel
            className="w-full"
            showArrows={true}
            showStatus={false}
            showThumbs={false}
            renderIndicator={(onClickHandler, isSelected, index) => (
                <li
                    className={`${iconColors[index % iconColors.length]} ${!isSelected ? "opacity-20" : ""} inline-block mx-[0.38rem] rounded-full w-[0.8rem] h-[0.8rem] cursor-pointer`}
                    onClick={onClickHandler}
                    key={index}
                />
            )}
        >
            {groupKeys.map((key, index) => (
                <div className="flex min-w-full w-full justify-start text-left pb-12" key={key ?? index}>
                    {renderSummarySection(summaryGroups?.[key] ?? summary)}
                </div>
            ))}
        </Carousel>
    );
};

export default ChargingSessionsSummary;

const formatNumber = (n, decimals = 0) => {
    const safe = Number.isFinite(n) ? n : 0;
    return safe.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

const mapSummaryToDisplay = (summary, t) => {
    const currency = summary?.currencyCode || "SEK";

    const totalTransactionAmount = Number(summary?.totalTransactionAmount ?? 0);
    const totalSessions = Number(summary?.totalSessions ?? 0);
    const successfulSessions = Number(summary?.successfulSessions ?? 0);
    const totalEnergyCharged = Number(summary?.totalEnergyCharged ?? 0);

    const averageDailySessions = Number(summary?.averageDailySessions ?? 0);
    const pluginSuccessRate = Number(summary?.pluginSuccessRate ?? 0);
    const averageChargingTimeMinutes = Number(summary?.averageChargingTimeMinutes ?? 0);
    const averageEnergyCharged = Number(summary?.averageEnergyCharged ?? 0);

    return {
        totalTransactionAmount: `${formatNumber(totalTransactionAmount, 0)} ${currency}`,
        totalSessions,
        successfulSessions,
        energyUsed: `${formatNumber(totalEnergyCharged, 2)} kWh`,
        averageDailySessions: formatNumber(averageDailySessions, 2),
        pluginSuccessRate: `${formatNumber(pluginSuccessRate, 2)}%`,
        averageChargingTime: `${formatNumber(averageChargingTimeMinutes, 2)} ${t("common.minutes")}`,
        averageEnergyUsed: `${formatNumber(averageEnergyCharged, 2)} kWh`,
    };
};
